/**
 * Shared asset-class detection.
 *
 * Four copies of the forex heuristic had drifted across `engine.ts`, `enrichment.ts`,
 * `fundamental.ts` and `routes/analysis.ts`. They disagreed in two ways:
 *
 *  - `enrichment.ts` compared `ticker.endsWith("=X")` without upper-casing first, so a
 *    lowercase symbol classified as forex in one module and as an equity in another.
 *  - All four accepted `ticker.length === 6 && !ticker.includes(".")`, which splits ANY
 *    dotless six-character ticker into a 3+3 currency pair (`ADANIY` -> `ADA`/`NIY`).
 *
 * This module is the single definition. It stays dependency-free so every layer — data
 * enrichment, the technical engine, the fundamental engine and the routes — can import it
 * without risking a circular dependency.
 */

/**
 * ISO 4217 codes the platform actually quotes pairs in. A bare six-letter symbol is only
 * treated as forex when BOTH halves appear here, which is what keeps six-letter equity and
 * ADR tickers out of the currency pathway.
 */
const CURRENCY_CODES = new Set([
  "USD", "EUR", "JPY", "GBP", "AUD", "NZD", "CAD", "CHF", "CNY", "CNH",
  "HKD", "SGD", "SEK", "NOK", "DKK", "ISK", "PLN", "CZK", "HUF", "RON",
  "BGN", "TRY", "RUB", "UAH", "ZAR", "MXN", "BRL", "ARS", "CLP", "COP",
  "PEN", "INR", "KRW", "TWD", "THB", "IDR", "MYR", "PHP", "VND", "ILS",
  "AED", "SAR", "QAR", "KWD", "BHD", "OMR", "EGP", "NGN", "KES", "MAD",
  "PKR", "BDT", "LKR"
]);

/** Provider classifications that positively exclude a symbol from the forex pathway. */
const NON_FOREX_QUOTE_TYPES = new Set([
  "EQUITY", "ETF", "MUTUALFUND", "INDEX", "FUTURE", "OPTION", "CRYPTOCURRENCY"
]);

/**
 * True when the symbol should be analysed as a foreign-exchange pair.
 *
 * Resolution order:
 *  1. An explicit `CURRENCY` classification from the provider always wins.
 *  2. Any other explicit provider classification is authoritative and excludes forex.
 *  3. Otherwise fall back to the symbol shape: a `=X` suffix, or a bare six-letter symbol
 *     whose two halves are both real ISO 4217 codes.
 */
export function isForexSymbol(ticker?: string | null, quoteType?: string | null): boolean {
  const qt = typeof quoteType === "string" ? quoteType.trim().toUpperCase() : "";
  if (qt === "CURRENCY") return true;
  if (NON_FOREX_QUOTE_TYPES.has(qt)) return false;

  if (typeof ticker !== "string") return false;
  const symbol = ticker.trim().toUpperCase();
  if (symbol.length === 0) return false;

  if (symbol.endsWith("=X")) return true;

  if (/^[A-Z]{6}$/.test(symbol)) {
    return CURRENCY_CODES.has(symbol.slice(0, 3)) && CURRENCY_CODES.has(symbol.slice(3, 6));
  }

  return false;
}

/**
 * True when the provider says this symbol is a pooled fund.
 *
 * The technical engine only ever holds `liveQuote`, so `quoteType` is the one fund signal
 * available there — `classifyFund` answers what a fund HOLDS, this answers whether it is one.
 * Keep them separate: the technical engine needs the second question and none of the first.
 */
export function isFundQuoteType(quoteType?: string | null): boolean {
  const qt = typeof quoteType === "string" ? quoteType.trim().toUpperCase() : "";
  return qt === "ETF" || qt === "MUTUALFUND";
}

/** Exposed for tests and for callers that need to validate a single leg of a pair. */
export function isCurrencyCode(code?: string | null): boolean {
  return typeof code === "string" && CURRENCY_CODES.has(code.trim().toUpperCase());
}

/**
 * Canonicalises a bare currency pair (`EURUSD`) to the provider's pair symbol (`EURUSD=X`).
 *
 * Anything that is not a validated pair is returned trimmed and upper-cased but otherwise
 * untouched. That matters more here than anywhere else: this runs at the HTTP boundary, so a
 * false positive rewrites a real equity ticker into a symbol that resolves to nothing and the
 * request fails before any lookup happens.
 */
export function normalizeForexTicker(ticker?: string | null): string {
  if (typeof ticker !== "string") return "";
  const symbol = ticker.trim().toUpperCase();
  if (symbol.length === 0 || symbol.endsWith("=X")) return symbol;
  return isForexSymbol(symbol, null) ? `${symbol}=X` : symbol;
}

/* ---------------------------------------------------------------------------------------------
 * Fund classification
 *
 * `etf.ts` classified funds by substring-matching ticker/name/summary, while `fundamental.ts`
 * independently derived equity/bond/commodity from `topHoldings` positions. Two classifiers over
 * the same fund, disagreeing, with the better-informed one confined to the indicator grid.
 *
 * This is the single definition. Resolution order is by reliability of the signal:
 *   1. Portfolio composition (`stockPosition` / `bondPosition` / `otherPosition`) — what the fund
 *      actually holds. Crypto is exempt: no position field separates bitcoin from gold.
 *   2. Provider category (`defaultKeyStatistics.category`, `fundProfile.categoryName`).
 *   3. Text over ticker / name / summary / industry — the last resort, not the first.
 * ------------------------------------------------------------------------------------------- */

export type FundAssetClass = "equity" | "fixedIncome" | "commodity" | "crypto" | "unknown";

export interface FundClassificationInput {
  ticker?: string | null;
  name?: string | null;
  summary?: string | null;
  industry?: string | null;
  /** `defaultKeyStatistics.category` or `fundProfile.categoryName`. */
  category?: string | null;
  stockPosition?: number | null;
  bondPosition?: number | null;
  otherPosition?: number | null;
}

export interface FundClassification {
  assetClass: FundAssetClass;
  /** Which tier resolved `assetClass`. Surfaced in the audit trace. */
  assetClassSource: "holdings" | "category" | "text" | "none";
  isEquityFund: boolean;
  isBondFund: boolean;
  isCommodityFund: boolean;
  isCryptoFund: boolean;
  /** Leveraged OR inverse — either makes a basket-multiple valuation meaningless. */
  isLeveraged: boolean;
  isInverse: boolean;
  /** Signed daily multiple where one is stated: 3 for a 3x bull, -3 for a 3x bear. */
  leverageFactor: number | null;
  isUraniumThematic: boolean;
}

/** A position only classifies the fund when it carries an outright majority. */
const POSITION_MAJORITY = 0.5;

/** Exact uranium/nuclear ticker matches. Substring matching caught TURN, BURN, and any other. */
const URANIUM_TICKERS = new Set(["ura", "urnm", "urnj", "uranium", "nlr", "nukz"]);

const lower = (v?: string | null) => (typeof v === "string" ? v.toLowerCase() : "");

/**
 * Direction and leverage.
 *
 * `short` alone is deliberately NOT an inverse signal: in fund names it far more often describes
 * maturity ("iShares Short Treasury Bond ETF", "Vanguard Short-Term Corporate Bond") than
 * direction, and treating it as inverse would route most of the front-end bond complex to the
 * wrong branch. Direction comes from `inverse`, `bear`, `ultrashort`, or a negative multiple.
 */
function resolveLeverage(text: string): Pick<FundClassification, "isLeveraged" | "isInverse" | "leverageFactor"> {
  // A bare `3x` / `-2x`, bounded so FLEX, SPXL and similar cannot yield a multiple.
  const match = text.match(/(?:^|[^a-z0-9])(-?[1-4])\s*x(?:[^a-z0-9]|$)/);
  const stated = match ? Number(match[1]) : null;

  const hasUltra = /\bultra(?:pro|short)?\b/.test(text);
  const isInverse =
    /\binverse\b/.test(text) ||
    /\bbear\b/.test(text) ||
    /\bultrashort\b/.test(text) ||
    (stated !== null && stated < 0);

  const magnitude = stated !== null ? Math.abs(stated) : null;
  const isLeveraged = isInverse || hasUltra || (magnitude !== null && magnitude > 1);

  let leverageFactor: number | null = null;
  if (magnitude !== null) leverageFactor = isInverse ? -magnitude : magnitude;

  return { isLeveraged, isInverse, leverageFactor };
}

/** Single source of truth for what a fund holds and how it is geared. */
export function classifyFund(input: FundClassificationInput): FundClassification {
  const ticker = lower(input.ticker).trim();
  const name = lower(input.name);
  const summary = lower(input.summary);
  const industry = lower(input.industry);
  const category = lower(input.category);
  const text = `${name} ${summary}`;

  const { isLeveraged, isInverse, leverageFactor } = resolveLeverage(`${ticker} ${text}`);

  const isUraniumThematic =
    URANIUM_TICKERS.has(ticker) ||
    name.includes("uranium") ||
    summary.includes("uranium") ||
    summary.includes("clean energy");

  // Crypto resolves ahead of the position tiers: a spot bitcoin ETP reports its holdings as
  // `otherPosition`, indistinguishable from a gold trust, so only the text says which it is.
  const isCryptoFund =
    ticker === "ibit" || ticker === "fbtc" || ticker === "gbtc" ||
    name.includes("bitcoin") || name.includes("ethereum") ||
    summary.includes("digital asset") || summary.includes("cryptocurrency");

  let assetClass: FundAssetClass = "unknown";
  let assetClassSource: FundClassification["assetClassSource"] = "none";

  if (isCryptoFund) {
    assetClass = "crypto";
    assetClassSource = "text";
  } else if ((input.stockPosition ?? 0) > POSITION_MAJORITY) {
    assetClass = "equity";
    assetClassSource = "holdings";
  } else if ((input.bondPosition ?? 0) > POSITION_MAJORITY) {
    assetClass = "fixedIncome";
    assetClassSource = "holdings";
  } else if ((input.otherPosition ?? 0) > POSITION_MAJORITY) {
    assetClass = "commodity";
    assetClassSource = "holdings";
  } else if (category) {
    // Equity is tested FIRST so a hybrid category resolves the way the ET-4 grid has always
    // resolved it: "Equity Energy" is a fund of energy *equities* and carries a real P/E, while
    // "Energy Limited Partnership" does not. Testing commodity first would flip the former.
    if (/equity|stock|blend|growth|value|market|sector/.test(category)) {
      assetClass = "equity";
    } else if (/bond|fixed income|treasury|municipal|credit|rate/.test(category)) {
      assetClass = "fixedIncome";
    } else if (/commodit|resource|metal|energy|natural resource/.test(category)) {
      assetClass = "commodity";
    }
    if (assetClass !== "unknown") assetClassSource = "category";
  }

  if (assetClass === "unknown") {
    if (
      ticker === "bnd" || ticker === "agg" || ticker === "lqd" || ticker === "hyg" ||
      name.includes("bond") || name.includes("fixed income") ||
      summary.includes("investment grade") || industry.includes("fixed income")
    ) {
      assetClass = "fixedIncome";
      assetClassSource = "text";
    } else if (
      ticker === "xme" || ticker === "gdx" || ticker === "gld" || ticker === "slv" ||
      summary.includes("mining") || summary.includes("commodity") || industry.includes("mining")
    ) {
      assetClass = "commodity";
      assetClassSource = "text";
    }
  }

  return {
    assetClass,
    assetClassSource,
    isEquityFund: assetClass === "equity",
    isBondFund: assetClass === "fixedIncome",
    isCommodityFund: assetClass === "commodity",
    isCryptoFund,
    isLeveraged,
    isInverse,
    leverageFactor,
    isUraniumThematic
  };
}

/**
 * The premium a fund's STRUCTURE justifies, as a fraction of NAV — the reference point a measured
 * premium has to be judged against, rather than zero.
 *
 * Shared by the valuation engine (`algoFairValue/etf.ts`) and the ET-1 indicator
 * (`fundamental.ts`) so the two cannot disagree about what "trading at its fair premium" means.
 * `fundamental.ts` must not import the valuation engine — `fvCountryRisk.test.ts` pins that by
 * scanning the source — so the constants live in this neutral module, exactly as `classifyFund`
 * does for fund routing.
 */
export const STRUCTURAL_NAV_PREMIUM = {
  /** Spot crypto ETPs: sustained premium from cash-redemption arbitrage friction. */
  crypto: 0.0020,
  /** Bond funds: NAV is struck at the bid, the exchange trades at the mid. */
  fixedIncome: 0.0015
} as const;

/**
 * Residual dislocation beyond the structural anchor that counts as material when the fund quotes
 * no spread to scale against. Also serves as the baseline noise floor for ET-1 band scaling
 * (v16.39) so tight liquid funds do not trigger on sub-basis-point auction noise.
 * Same constant the valuation engine uses for `isEtfNavDislocated`.
 */
export const NAV_DISLOCATION_THRESHOLD = 0.0050;

/**
 * How many half-spreads a residual must clear before it is a dislocation rather than market-maker
 * friction. A quoted spread is the width of the band inside which a price is not saying anything,
 * so a signal has to sit outside it.
 */
export const NAV_DISLOCATION_SPREAD_MULTIPLE = 2.0;

/**
 * Minimum structural tolerance on the bearish side (v16.39). An ETF premium is a natural structural
 * feature rather than an immediate defect, so a fund is never penalized on the premium side unless
 * its residual clears both its liquidity-scaled band and this 1.00% structural barrier:
 * max(band, 1.00%).
 */
export const NAV_PREMIUM_BEARISH_FLOOR = 0.0100;
/** Backwards-compatible alias for NAV_PREMIUM_BEARISH_FLOOR. */
export const NAV_PREMIUM_BEARISH_CAP = NAV_PREMIUM_BEARISH_FLOOR;

/**
 * The structural premium for a fund, given its class. Bond funds prefer their OWN bid-to-mid
 * half-spread, since that is the mechanism generating the optical premium — NAV struck at the bid
 * against an exchange trading at the mid — and fall back to the constant when no spread is quoted.
 *
 * Returns 0 for classes with no established anchor. Two categories a practitioner review flagged
 * as carrying legitimate persistent premiums are deliberately NOT anchored here yet, because no
 * calibrated magnitude exists for them and inventing one would be worse than measuring against
 * zero: international equity funds whose NAV is struck in an earlier time zone, and physical
 * commodity trusts under supply constraint. Neither is currently detectable from what the data
 * provider returns either — an anchor for them needs both a detector and a magnitude.
 */
export function structuralNavPremium(
  fund: { isCryptoFund: boolean; isBondFund: boolean },
  bidMidSpread?: number
): number {
  if (fund.isCryptoFund) return STRUCTURAL_NAV_PREMIUM.crypto;
  if (fund.isBondFund) {
    return bidMidSpread !== undefined && bidMidSpread > 0
      ? bidMidSpread
      : STRUCTURAL_NAV_PREMIUM.fixedIncome;
  }
  return 0;
}

/**
 * Sector volatility buckets, used by the technical engine to widen or tighten two Bollinger
 * branches: `isLowBeta` narrows the band to 1.5 sigma, and `isHighBeta` demands a 1.5x volume
 * expansion before crediting an upside band break.
 *
 * BOTH TAXONOMIES ARE LISTED DELIBERATELY. The quote feed returns Yahoo's sector labels -
 * "Consumer Cyclical", "Consumer Defensive", "Financial Services", "Basic Materials" - which is
 * what every other module in this repo matches on. These lists originally carried only the GICS
 * spellings, so "Consumer Discretionary" and "Consumer Staples" never matched a real quote and two
 * of the seven entries were dead code: consumer cyclicals silently skipped the high-beta breakout
 * suppressor, and consumer defensives silently ran the 2.0-sigma band meant for ordinary names.
 *
 * The lesson generalises past this fix: when you hard-code a vocabulary supplied by an external
 * feed, check it against the vocabulary the feed actually emits, not against the standard it is
 * nominally derived from.
 */
const HIGH_BETA_SECTORS = new Set([
  "Technology",
  "Communication Services",
  "Consumer Discretionary", // GICS
  "Consumer Cyclical",      // Yahoo
]);

const LOW_BETA_SECTORS = new Set([
  "Utilities",
  "Healthcare",
  "Real Estate",
  "Consumer Staples",   // GICS
  "Consumer Defensive", // Yahoo
]);

/**
 * Sectors whose capital intensity makes a high capex-to-depreciation ratio normal rather than
 * distressed, gating the Heavy Industrial Capex Normalization in `enrichSymbolData`.
 *
 * THE THIRD INSTANCE OF THE SAME TRAP, found 2026-09-20. `enrichment.ts` tested
 * `sector === "Materials"` - the GICS spelling. The quote feed emits **"Basic Materials"**, so the
 * branch never fired for a single miner, chemical producer or steelmaker, and those are precisely
 * the businesses that buy a mine or a mill in one year and read as distressed for it. The two
 * neighbouring conditions ("Industrials", "Energy") happen to be spelled identically in both
 * taxonomies, which is why the defect survived beside working code.
 *
 * Note the line immediately above it in the same file already paired
 * `"Financial Services"` with `"Financials"`. The vocabulary was being handled correctly a few
 * hundred lines away from where it was not, which is the argument for one predicate rather than
 * per-site literals.
 */
const HEAVY_CAPEX_SECTORS = new Set([
  "Industrials",      // same spelling in both taxonomies
  "Energy",           // same spelling in both taxonomies
  "Basic Materials",  // Yahoo — the spelling the feed actually returns
  "Materials",        // GICS — preserved so no existing behaviour is withdrawn
]);

export function isHighBetaSector(sector?: string | null): boolean {
  return HIGH_BETA_SECTORS.has((sector || "").trim());
}

export function isHeavyCapexSector(sector?: string | null): boolean {
  return HEAVY_CAPEX_SECTORS.has((sector || "").trim());
}

export function isLowBetaSector(sector?: string | null): boolean {
  return LOW_BETA_SECTORS.has((sector || "").trim());
}
